import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { FileService } from 'app/_services/file.service';
import { NotificationService } from 'app/_services/notification.service';
import { SiteAssetsService } from 'app/_services/site-assets.service';
import { SiteSessionsService } from 'app/_services/site-sessions.service';
import { SiteWorkOrdersService } from 'app/_services/site-work-orders.service';
import DateTimeUtils from 'app/_utils/datetime.utils';
import Swal from 'sweetalert2';
import { AddAvailabilityModalComponent } from '../add-availability-modal/add-availability-modal.component';
import { EditAvailabilityModalComponent } from '../edit-availability-modal/edit-availability-modal.component';
import { RestrictionsType } from 'app/_models/restrictions/restrictions-type';

@Component({
  selector: 'asset-page',
  templateUrl: './asset-page.component.html',
})
export class AssetPageComponent implements OnInit {
  private selectedSiteId: any;
  private assetId: any;
  private routeRole: any;

  public asset: any;
  public site: any;
  public workOrders: any;
  public imageUrl: any;
  public isTurbineAsset = false;

  public allowAssetScheduling = false;
  public exemptFromAssetScheduling: boolean;
  public restrictionType: { label: string; value: RestrictionsType  }[] = [
    { label: 'Operational', value: RestrictionsType.Operational },
    { label: 'Health & Safety', value: RestrictionsType.HealthSafety },
    { label: 'General', value: RestrictionsType.General },
  ];

  public isAddRestrictionModalShown = false;
  public addAssetRestrictionForm: FormGroup;
  public isSaving = false; // helps prevent double click submit

  constructor(
    private modalService: NgbModal,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private assetsService: SiteAssetsService,
    private siteSessionsService: SiteSessionsService,
    private workOrderService: SiteWorkOrdersService,
    private fileService: FileService,
    private router: Router,
    globals: Globals,
    fb: FormBuilder,
  ) {
    this.routeRole = globals.user.routeRole;

    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
      this.assetId = params.assetId;
    });
    this.addAssetRestrictionForm = fb.group({
      description: new FormControl('', Validators.required),
      type: new FormControl('', Validators.required),
      lockAsset: new FormControl(),
    });
  }

  ngOnInit() {
    this.setupPage();
  }

  public setupPage() {
    this.assetsService.getAssetById(this.assetId).subscribe(asset => {

      this.asset = asset;
      this.site = this.asset.site;
      this.allowAssetScheduling = this.asset.site.siteOwner.siteOwnerUser.allowAssetScheduling;
      this.exemptFromAssetScheduling = this.asset.exemptFromAssetScheduling;
      this.prepareTheAsset();
      if(this.asset.locked){
        this.addAssetRestrictionForm.get('lockAsset').disable();
      }

      this.workOrderService.getAllApprovedWorkOrdersByAsset(this.asset.id).subscribe(workOrders => {
        this.workOrders = workOrders;
        this.workOrders.forEach(workOrder => {
          workOrder.wrkId = 'WRK' + workOrder.id;
        });
      })
    });
  }

  private prepareTheAsset() {
    this.asset.assetCategories.sort((a, b) => a.name.localeCompare(b.name));
    this.isTurbineAsset = Array.isArray(this.asset.assetCategories) && this.asset.assetCategories.some(category =>
      category.name.toLowerCase() === 'turbine'
    );

    this.fileService.getAssetImage(this.asset.imageUrl).subscribe(image => {
      this.imageUrl = image.safeUrl;
    });

    this.asset.assetCertificates = this.asset.assetCertificates.filter(cert => !cert.deleted);
    this.asset.assetCertificates
      .sort((a, b) => {
        return new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime();
      })
      .forEach(certificate => {
        certificate.expired = DateTimeUtils.isDateStrExpired(certificate.expiryDate);
      });

    this.asset.assetAvailability
      .sort((a, b) => {
        return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
      });
  }

  public editAssetDetails() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/indi-asset', { type: 'edit', assetId: this.asset.id }]);
  }

  public deleteAsset() {
    Swal.fire({
      title: 'Delete Asset?',
      text: 'You will not be able to revert this and may affect outstanding Work Orders!',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.value) {
        this.assetsService.deleteAsset(this.asset.id).subscribe(() => {
          this.notificationService.notify('success', 'Successfully Deleted');

          this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/']);
        });
      }
    });
  }

  public alterLockStatus() {
    if (this.asset.locked) {
      let warningText = 'Are you sure you want to unlock this asset?';
      this.siteSessionsService.getActiveSiteSessionByAsset(this.asset.id).subscribe((data: any) => {
        if (data.length) {
          warningText = `It looks like there is a live work session underway at this asset!
          Unlocking this asset may result in serious risk or injury to on site workers.
          Are you sure you want to unlock this asset? `;

        }
        Swal.fire({
          title: 'Unlock Asset',
          text: warningText,
          type: 'warning',
          showCancelButton: true,
          customClass: {
            confirmButton: 'confirm-danger',
          },
          confirmButtonText: 'Yes, unlock!',
        }).then((result) => {
          if (result.value) {
            this.changeLockStatusTo(false);
          }
        });
      });

    } else {
      Swal.fire({
        title: 'Lock Asset',
        text: 'Are you sure you want to lock this asset? No work orders will be accessible until unlock.',
        type: 'warning',
        showCancelButton: true,
        customClass: {
          confirmButton: 'confirm-danger',
        },
        confirmButtonText: 'Yes, lock asset!',
      }).then((result) => {
        if (result.value) {
          this.changeLockStatusTo(true);
        }
      });
    }
  }

  private changeLockStatusTo(value: boolean) {
    const body = {
      assetId: this.asset.id,
      locked: value,
    };
    this.assetsService.setAssetLockStatus(body).subscribe(() => {
      Swal.fire('Success', 'Asset lock status updated!', 'success');

      this.setupPage();
    });
  }

  public editCertificates() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/' + this.asset.id + '/certificates']);
  }

  public goToWorkOrder(workOrderId) {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/work-orders/' + workOrderId]);
  }

  public viewScheduleHistory() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/assets/' + this.asset.id + '/availability']);
  }

  public editScheduleItem(scheduleItem) {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
    const modal = this.modalService.open(EditAvailabilityModalComponent, modalOptions);
    modal.componentInstance.scheduleItem = scheduleItem;
    modal.componentInstance.assetId = this.assetId;

    modal.result.then(result => {
      if (result === 'refresh') {
        this.setupPage();
      }
    }).catch(() => { /* modal dismissed */ });
  }

  public addScheduleItem() {
    const modalOptions: NgbModalOptions = { centered: true, backdrop: 'static' };
    const modal = this.modalService.open(AddAvailabilityModalComponent, modalOptions);
    modal.componentInstance.assetId = this.assetId;

    modal.result.then(result => {
      if (result === 'refresh') {
        this.setupPage();
      }
    }).catch(() => { /* modal dismissed */ });

  }

  public goToSchedulerPage() {
    this.router.navigate(['/' + this.routeRole + '/sites/' + this.selectedSiteId + '/schedule']);
  }

  public removeRestriction(restrictionId :number){
    Swal.fire({
      title: 'Remove Asset Restriction?',
      type: 'warning',
      showCancelButton: true,
      customClass: {
        confirmButton: 'confirm-danger',
      },
      confirmButtonText: 'Yes, remove it!',
    }).then((result) => {
      if (result.value) {
        this.assetsService.removeAssetRestriction(restrictionId).subscribe(result => {
          this.setupPage();
        });
        Swal.fire('Success', 'Asset Restriction Removed!', 'success');
      }
    });

  }
  public addRestriction(){
    this.addAssetRestrictionForm.reset();
    this.isAddRestrictionModalShown = true;
  }

  public async onAddRestrictionFormSubmit() {
    this.isSaving = true;

    const restriction = {
      description: this.addAssetRestrictionForm.value.description,
      type: this.addAssetRestrictionForm.value.type.value,
      locksAsset: this.addAssetRestrictionForm.value.lockAsset,
      assetId: this.assetId,
    };

    this.assetsService.addAssetRestriction(restriction).subscribe(() => {
      this.isSaving = false;
      this.isAddRestrictionModalShown = false;
      this.setupPage();
      this.notificationService.notify('success', 'Success', 'New Asset Restriction Added');
    });
  }
}
